import { createRouter, createWebHistory } from "vue-router";
import { userStore } from "@/stores/userStore";
import { useToast } from "vue-toastification";
import { PERMISSION } from "@/constant/permission";
import { checkPermissionList } from "@/utils/permissionUtils";

const routes = [
  {
    path: "/:slug/callback",
    name: "SSOCallback",
    meta: { title: "SSO Callback" },
    component: () => import("@/views/jns/view/login/index.vue"),
  },
  {
    path: "/:param?",
    name: "Login",
    meta: { title: "Login" },
    component: () => import("@/views/jns/view/login/index.vue"),
  },
  {
    path: "/email/verify/:userId/:email/:tokenEmail",
    name: "VerifiedEmail",
    meta: { title: "Verified Email" },
    props: true,
    component: () => import("@/views/jns/view/verifiedemail/index.vue"),
  },
  {
    path: "/app",
    name: "Layout",
    meta: { title: "Layout" },
    redirect: "/app/dashboard",
    component: () => import("@/Layout/index.vue"),
    children: [
      {
        path: "token",
        name: "View Token",
        meta: { title: "Token", permissions: [PERMISSION.token.list] },
        component: () => import("@/views/jns/view/viewtoken/index.vue"),
      },
      {
        path: "dashboard",
        name: "Dashboard",
        meta: {
          title: "Dashboard",
          permissions: [
            PERMISSION.dashboard.submission,
            PERMISSION.dashboard.count,
            PERMISSION.dashboard.chartSubmission,
            PERMISSION.dashboard.chartTraffic,
          ],
        },
        component: () => import("@/views/jns/view/dashboard/index.vue"),
      },
      {
        path: "messaging",
        name: "Messaging",
        meta: { title: "Messaging", permissions: [PERMISSION.task.list] },
        component: () => import("@/views/jns/view/messaging/index.vue"),
      },
      {
        path: "current-transaction",
        name: "Current Transaction",
        meta: {
          title: "Report",
          permissions: [PERMISSION.report.current.list],
        },
        component: () =>
          import("@/views/jns/view/report/currenttransaction/index.vue"),
      },
      {
        path: "request-transaction",
        name: "Request Transaction",
        meta: {
          title: "Report",
          permissions: [PERMISSION.report.request.list],
        },
        component: () =>
          import("@/views/jns/view/report/requesttransaction/index.vue"),
      },
      {
        path: "blacklist",
        name: "Blacklist",
        meta: {
          title: "Settings",
          permissions: [
            PERMISSION.setting.blacklist.list,
            PERMISSION.setting.blacklist.add,
            PERMISSION.setting.blacklist.edit,
            PERMISSION.setting.blacklist.delete,
          ],
        },
        component: () =>
          import("@/views/jns/view/settings/blacklist/index.vue"),
      },
      {
        path: "phonebook",
        name: "Phonebook",
        meta: {
          title: "Settings",
          permissions: [PERMISSION.setting.phonebook.list],
        },
        component: () =>
          import("@/views/jns/view/settings/phonebook/index.vue"),
      },
      {
        path: "phonebook-detail",
        name: "Phonebook Detail",
        meta: {
          title: "Settings",
          permissions: [PERMISSION.setting.phonebookDetail.list],
        },
        component: () =>
          import("@/views/jns/view/settings/phonebookdetail/index.vue"),
      },
      {
        path: "view-masking",
        name: "View Masking",
        meta: {
          title: "Settings",
          permissions: [PERMISSION.setting.masking.list],
        },
        component: () =>
          import("@/views/jns/view/settings/viewmasking/index.vue"),
      },
      {
        path: "single",
        name: "Single",
        meta: {
          title: "Messaging",
          permissions: [PERMISSION.single.config],
        },
        component: () =>
          import("@/views/jns/view/messaging/singlesetup/index.vue"),
      },
      {
        path: "personal",
        name: "Personal",
        meta: { title: "Messaging", permissions: [PERMISSION.personal.config] },
        component: () =>
          import("@/views/jns/view/messaging/personalsetup/index.vue"),
      },
      {
        path: "personal-schedule",
        name: "Personal Schedule",
        meta: {
          title: "Messaging",
          permissions: [PERMISSION.scheduled.submit],
        },
        component: () =>
          import("@/views/jns/view/messaging/personalschedulersetup/index.vue"),
      },
    ],
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  base: process.env.BASE_URL,
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { top: 0 };
    }
  },
});

router.beforeEach((to, from, next) => {
  const user = userStore();
  const toast = useToast();
  const isAuthenticated = user.getToken != null;

  const publicPageName = ["Login", "VerifiedEmail", "SSOCallback"];
  const toPublicPage = publicPageName.includes(to.name);

  if (!toPublicPage && !isAuthenticated) next({ name: "Login" });
  else if (to.name === "VerifiedEmail" && isAuthenticated) {
    user.setAllData(null, {}, null);
    document.location.reload();
  } else if (toPublicPage && isAuthenticated) next({ name: "Dashboard" });
  else {
    if (to.meta.permissions !== undefined) {
      if (checkPermissionList(to.meta.permissions)) {
        next();
        return;
      } else {
        toast.error("you don't have permission to view this page", {
          timeout: 2000,
        });
        next({ name: "Dashboard" });
        return;
      }
    } else {
      next();
      return;
    }
  }
});

router.afterEach(() => {
  // Remove initial loading
  const appLoading = document.getElementById("loading-bg");
  if (appLoading) {
    appLoading.style.display = "none";
  }
});

export default router;
